/**
 * Disclosure shown before any personal data is sent to Google's Gemini API.
 *
 * This copy is what App Store guideline 5.1.2(i) is satisfied by, so it is kept
 * here as data and unit-tested rather than buried in JSX. Three rules govern
 * edits, all load-bearing:
 *
 *  1. It must say "full entry history" — formatEntriesForContext really does
 *     send every entry, and softening this makes the sheet inaccurate.
 *  2. It must name Google, not "a third-party AI provider".
 *  3. It must not imply end-to-end encryption; entries are decrypted server-side.
 *
 * Materially changing any of it means bumping CONSENT_VERSION in both
 * src/lib/ai-consent.ts and convex/aiConsent.ts so everyone is re-asked.
 */

export type ConsentFlow = {
  /** Short label, bolded in the sheet. */
  label: string;
  /** What is sent and why. */
  detail: string;
};

export const AI_CONSENT_TITLE = "AI features and your data";

export const AI_CONSENT_INTRO =
  "Jilu uses Google's Gemini AI for some features. Before you turn them on, here's exactly what gets sent.";

export const AI_CONSENT_FLOWS: ConsentFlow[] = [
  {
    label: "Photos you attach",
    detail: "the image itself, to generate a short description",
  },
  {
    label: "Messages you write when logging",
    detail: "to pull out tags, symptoms and severity ratings",
  },
  {
    label: "Your full entry history",
    detail:
      "when you use Insights chat: your notes, tags, severity ratings and dates, so it can answer questions about your patterns",
  },
];

export const AI_CONSENT_RECIPIENT =
  "Google, through the Gemini API. Google doesn't use this data to train its AI models. Your entries are decrypted on our servers before being sent, so this isn't end-to-end encrypted.";

export const AI_CONSENT_DECLINE =
  "You can still log entries, attach photos, and see your trends and top mentions. You won't get automatic tags, photo descriptions or Insights chat. You can change this any time in Settings.";

export const AI_CONSENT_ACCEPT_LABEL = "Allow";
export const AI_CONSENT_DECLINE_LABEL = "Not now";
